import { useState, type SyntheticEvent } from "react";
import { useAuthActions } from "@convex-dev/auth/react";
import { useMutation } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../ui/button";
import { errorMessage } from "../workspace/format";
import { LegalLinks, SITE_URL } from "./AuthScreen";

type Me = Exclude<FunctionReturnType<typeof api.workspace.me>, { status: "signed-out" | "passkey-required" | "ready" }>;

function longDate(timestamp: number) {
  return new Date(timestamp).toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

export function Onboarding({ me }: { me: Me }) {
  const { signOut } = useAuthActions();
  const bootstrap = useMutation(api.workspace.bootstrap);
  const createFromGrant = useMutation(api.workspace.createFromGrant);
  const accept = useMutation(api.workspace.acceptInvitation);
  const grant = me.status === "needs-workspace" ? me.grant : undefined;
  const [workspaceName, setWorkspaceName] = useState(grant?.workspaceName ?? "");
  const [displayName, setDisplayName] = useState(me.user.name);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (me.status === "needs-workspace") {
        if (grant !== undefined) await createFromGrant({ workspaceName, displayName });
        else await bootstrap({ workspaceName, displayName });
      } else if (me.status === "invited") await accept({ workspaceId: me.workspace.id, displayName });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const selfServe = me.status !== "invited" && me.selfServe;
  const invitedOnlyName = (me.status === "not-invited" ? me.workspace : undefined)?.name ?? "This workspace";
  return (
    <main className="auth-screen" id="main-content">
      <form className="auth-card" onSubmit={(event) => { void submit(event); }}>
        <div className="brand-mark" aria-label="Yodl"><span>Y</span></div>
        {me.status === "needs-workspace" ? (
          <>
            <h1>Create your workspace</h1>
            {grant !== undefined ? (
              <p className="auth-lede">
                {grant.trial
                  ? `Your 14-day free trial starts the moment you create the workspace${grant.trialEndsAt !== undefined ? `; your card is charged on ${longDate(grant.trialEndsAt)} unless you cancel first` : ""}. Protected health information stays off during the trial. You are the owner and can invite up to ${String(grant.plan.users)} people.`
                  : `Your Yodl plan is active. You are the owner and can invite up to ${String(grant.plan.users)} people.`}
              </p>
            ) : (
              <p className="auth-lede">You are the first person here, so you become the owner. You can invite everyone else afterwards.</p>
            )}
            <label>Workspace name<input name="workspace-name" autoComplete="organization" value={workspaceName} onChange={(event) => { setWorkspaceName(event.target.value); }} placeholder="Northstar Health" minLength={2} maxLength={60} required /></label>
            <label>Your name<input name="display-name" autoComplete="name" value={displayName} onChange={(event) => { setDisplayName(event.target.value); }} placeholder="Alex Rivera" required /></label>
          </>
        ) : me.status === "invited" ? (
          <>
            <h1>You’re invited to {me.workspace.name}</h1>
            <p className="auth-lede">Signed in as {me.user.email}. Confirm the name your teammates will see.</p>
            <label>Your name<input name="display-name" autoComplete="name" value={displayName} onChange={(event) => { setDisplayName(event.target.value); }} placeholder="Alex Rivera" required /></label>
          </>
        ) : selfServe ? (
          <>
            <h1>No Yodl plan or invitation for {me.user.email}</h1>
            <p className="auth-lede">Start a free trial at yodl.cc using this email address, or ask your workspace owner to invite this exact address. Then sign in again.</p>
            <a className="button button--primary" href={`${SITE_URL}/pricing/`}>Start a free trial</a>
          </>
        ) : (
          <>
            <h1>No invitation for {me.user.email}</h1>
            <p className="auth-lede">{invitedOnlyName} is invite-only. Ask an admin to invite this exact email address, then sign in again.</p>
          </>
        )}
        {error !== null ? <p className="form-error" role="alert">{error}</p> : null}
        {me.status !== "not-invited" ? <Button type="submit" variant="primary" disabled={busy}>{busy ? "One moment…" : me.status === "needs-workspace" ? (grant?.trial === true ? "Create workspace and start trial" : "Create workspace") : "Join workspace"}</Button> : null}
        <button type="button" className="link-button" onClick={() => { void signOut(); }}>Sign out</button>
        <LegalLinks />
      </form>
    </main>
  );
}
